import type { RepairSkill } from '../../../battle/types';
import { playSfx } from '../../../render/utils/audio';
import type { SkillBundle } from '../../types';

const ROOT = '/assets/textures/characters/sparky_bot/skills/repair';

// Sheet layout - same 4x4 convention as the other skill / character sheets.
const SHEET_COLS = 4;
const SHEET_ROWS = 4;
// Defaults - overridable per character via timings.skills.repair.
// IMPACT_FRAME: frame at which the heal "lands" (renderer pops the +N / fills
// the HP bar there). ANIM_SPEED: sheet playback speed.
const IMPACT_FRAME = 8;
const ANIM_SPEED = 1;

const ENGINE: RepairSkill = {
  kind: 'repair',
  id: 'repair',
  name: 'Repair',
  // when charged, heals 25% of maxHP on the own turn (attack still follows)
  healPercent: 0.25,
  animationId: 'repair',
};

export const REPAIR: SkillBundle = {
  engine: ENGINE,
  assets: {
    sheets: {
      repair: { url: `${ROOT}/repair_v2.png`, cols: SHEET_COLS, rows: SHEET_ROWS },
    },
    // Shown under the UI-log "REPAIR" label. Reserved key: logIcon.
    textures: {
      logIcon: `${ROOT}/ui-log-icon.png`,
      logCircleIcon: `${ROOT}/ui-log-circle-icon.png`,
    },
    sfx: {
      repair: `${ROOT}/repair.mp3`,
    },
  },
  async playSelf(ctx) {
    const { actor, assets } = ctx;
    const frames = assets.sheets.repair;
    if (!frames) return;

    // Per-character overrides (speed / offset / scale / heal frame) fall back
    // to the skill's built-in defaults.
    const t = actor.timings.skills?.[ENGINE.id];
    const impactFrame = t?.impactFrame ?? IMPACT_FRAME;

    // In-place self animation - no lunge, no defender. Recovery frames keep
    // playing after we return; schedule the idle snap so it doesn't freeze
    // on the last frame.
    actor.playFrames(frames, {
      loop: false,
      speed: t?.speed ?? ANIM_SPEED,
      offsetX: t?.offsetX,
      offsetY: t?.offsetY,
      scaleMultiplier: t?.scale,
    });
    actor.scheduleIdleAfterCurrentAnim();

    const sfx = assets.sfx.repair;
    if (sfx) playSfx(sfx);

    // Return at the heal frame so the renderer's following `heal` event pops
    // the +N / fills the HP bar mid-anim (recovery frames continue behind it).
    await actor.awaitFrame(impactFrame);
  },
};
